import { ApiErrorResponse } from '@/types';
import { AxiosError } from 'axios';

/**
 * Extract detailed error message from API error response.
 * Handles Laravel validation errors with the "errors" attribute.
 *
 * @param error - The error object (typically from Axios or TanStack Query)
 * @param fallbackMessage - Default message if no specific error is found
 * @returns Formatted error message string
 *
 * @example
 * // Returns: "Validation Error: The email field is required. The password must be at least 8 characters."
 * extractErrorMessage(axiosError)
 *
 * @example
 * // Returns: "The operational office country field format is invalid."
 * extractErrorMessage(validationError)
 */
export function extractErrorMessage(
  error: unknown,
  fallbackMessage = 'An unexpected error occurred'
): string {
  // Handle Axios errors
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;

    if (axiosError.response?.data) {
      const responseData = axiosError.response.data;

      // Extract validation errors from "errors" attribute
      if (responseData.errors && Object.keys(responseData.errors).length > 0) {
        const validationErrors = Object.entries(responseData.errors)
          .map(([field, messages]) => {
            // Format field name (e.g., "operational_office_country" -> "Operational Office Country")
            const formattedField = field
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');

            // Join all messages for this field
            return `${formattedField}: ${messages.join(' ')}`;
          })
          .join('\n');

        // Include the main message if it exists and is meaningful
        if (
          responseData.message &&
          responseData.message !== 'Validation Error'
        ) {
          return `${responseData.message}\n${validationErrors}`;
        }

        return validationErrors;
      }

      // Return the message from response if available
      if (responseData.message) {
        return responseData.message;
      }
    }

    // Handle network errors
    if (axiosError.message) {
      return axiosError.message;
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Fallback message
  return fallbackMessage;
}

/**
 * Extract error messages as an array for programmatic handling.
 * Useful when you need to display errors as a list.
 *
 * @param error - The error object
 * @returns Array of error messages
 *
 * @example
 * // Returns: ["The email field is required.", "The password must be at least 8 characters."]
 * extractErrorMessages(axiosError)
 */
export function extractErrorMessages(error: unknown): string[] {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;

    if (axiosError.response?.data?.errors) {
      const errors = axiosError.response.data.errors;
      const messages: string[] = [];

      Object.entries(errors).forEach(([field, fieldMessages]) => {
        const formattedField = field
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        fieldMessages.forEach(msg => {
          messages.push(`${formattedField}: ${msg}`);
        });
      });

      return messages;
    }

    if (axiosError.response?.data?.message) {
      return [axiosError.response.data.message];
    }

    if (axiosError.message) {
      return [axiosError.message];
    }
  }

  if (error instanceof Error) {
    return [error.message];
  }

  if (typeof error === 'string') {
    return [error];
  }

  return ['An unexpected error occurred'];
}
